import React from 'react';
import { SwapWidget, Theme } from '@uniswap/widgets';
import '@uniswap/widgets/fonts.css';
import { motion } from 'framer-motion';
import { ArrowDownUp, Info, Sparkles } from 'lucide-react';

const customTheme: Partial<Theme> = {
  primary: '#8b5cf6',
  secondary: '#71717a',
  interactive: '#a78bfa', 
  container: '#18181b',
  module: '#27272a',
  accent: '#8b5cf6',
  outline: '#3f3f46',
  dialog: '#18181b',
  scrim: '#000000',
  
  onAccent: '#ffffff',
  onInteractive: '#ffffff',
  
  borderRadius: { xsmall: 0.25, small: 0.5, medium: 0.75, large: 1 },
  fontFamily: 'Inter, system-ui, sans-serif',
  
  networkDefaultShadow: 'rgba(139, 92, 246, 0.5)',
};

interface UniswapWidgetProps {
  defaultInputToken?: string;
  defaultOutputToken?: string;
}

const UniswapWidget: React.FC<UniswapWidgetProps> = ({
  defaultInputToken = 'ETH',
  defaultOutputToken = 'USDC',
}) => {
  // Using mainnet token addresses
  const ETH_ADDRESS = '0x0000000000000000000000000000000000000000';
  const USDC_ADDRESS = '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-6 relative overflow-hidden"
    >
      {/* Gradient background effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-violet-600/10 via-transparent to-pink-600/10 pointer-events-none" />
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <ArrowDownUp className="w-5 h-5 text-violet-400" />
            Quick Swap
            <Sparkles className="w-4 h-4 text-amber-400" />
          </h2>
          <div className="group relative">
            <Info className="w-4 h-4 text-zinc-400 cursor-help" />
            <div className="absolute right-0 top-6 w-64 bg-zinc-900 border border-zinc-700 rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
              <p className="text-xs text-zinc-400">
                Swap tokens directly through Uniswap. Connect your wallet to execute real trades with the best available rates.
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <div className="w-full max-w-md">
            <SwapWidget
              theme={customTheme}
              defaultInputTokenAddress={ETH_ADDRESS}
              defaultOutputTokenAddress={USDC_ADDRESS}
              width="100%"
            />
          </div>
        </div>

        <div className="mt-6 p-4 bg-gradient-to-r from-violet-600/20 to-pink-600/20 rounded-lg border border-violet-600/30">
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-zinc-300">
              <p className="font-medium text-violet-300 mb-1">Powered by Uniswap</p>
              <p className="text-zinc-400">
                Get the best rates across multiple liquidity sources. Gas fees and slippage are calculated automatically.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default UniswapWidget;